/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument, RatesDocument } from 'paintscout'
// Deep import, not the `../../quote/tiered-packages` barrel: that barrel is reached from this folder
// already (to-tiered-packages) and closing the loop through it leaves sibling exports undefined
// depending on module init order — the hazard apply-tier-products documents.
import { setTieredPackages } from '../../quote/tiered-packages/set-tiered-packages'
import type { QuoteTemplateOption } from '../index'
import { getTemplateTieredPackages } from './get-template-tiered-packages'

/**
 * Give a new estimate the packages its quote template links, switched on.
 *
 * Linking a package template to a quote template is the whole instruction, so an estimate built
 * from that template arrives with packages on rather than making the estimator turn them on by
 * hand (ENG-1630). The set is resolved live, so it is the package template as it stands now, not as
 * it stood when the link was made (ENG-1629).
 *
 * Call once the estimate's items and totals exist. A package set with product rules prices Basic and
 * Premium by recalculating the estimate with the swapped products, and there is nothing to
 * recalculate until the template's items have landed — without that pass the two outer tiers would
 * show the middle tier's price.
 *
 * A no-op when the quote template links nothing, or when its link no longer resolves.
 */
export function applyTemplateTieredPackages(args: {
  quote: QuoteDocument
  /** The quote template's options entry, which carries the link. */
  template?: QuoteTemplateOption | null
  options: OptionsDocument
  /** For the tier swap's coverage rule — see swapTierProducts. */
  rates?: RatesDocument | null
}): QuoteDocument {
  const { quote, template, options, rates } = args

  // A template being authored keeps packages off — that is the state it hands down, not one it uses.
  // An invoice has nothing to choose: the customer has already accepted a package.
  if (quote?.type === 'quoteTemplate' || quote?.is_invoice) {
    return quote
  }

  const tieredPackages = getTemplateTieredPackages({ template, quote: template?.content, options })

  if (!tieredPackages) {
    return quote
  }

  return setTieredPackages({ quote, options, rates, tieredPackages })
}
